/**
 * Constant-time comparison of two Uint8Arrays.
 *
 * Security-critical: This function MUST NOT short-circuit.
 * It compares all bytes regardless of where a mismatch occurs,
 * preventing timing side-channel attacks.
 *
 * On Node.js: delegates to crypto.timingSafeEqual
 * On browsers: uses XOR accumulation
 */

let _timingSafeEqual: ((a: Uint8Array, b: Uint8Array) => boolean) | null = null;

/**
 * Attempt to load Node.js crypto.timingSafeEqual at module load time.
 * Falls back to XOR-based comparison in non-Node environments.
 */
function getNodeTimingSafeEqual(): ((a: Uint8Array, b: Uint8Array) => boolean) | null {
  if (_timingSafeEqual !== null) return _timingSafeEqual;

  try {
    // Check for Node.js environment
    if (
      typeof globalThis.process !== 'undefined' &&
      typeof globalThis.process.versions !== 'undefined' &&
      typeof globalThis.process.versions.node === 'string'
    ) {
      // Dynamic import would be async; use require for sync access
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const crypto = require('node:crypto') as {
        timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean;
      };
      if (typeof crypto.timingSafeEqual === 'function') {
        _timingSafeEqual = crypto.timingSafeEqual;
        return _timingSafeEqual;
      }
    }
  } catch {
    // Not in Node.js or crypto not available
  }

  return null;
}

/**
 * XOR-based constant-time comparison for browser environments.
 * Iterates over ALL bytes — never short-circuits.
 */
function xorCompare(a: Uint8Array, b: Uint8Array): boolean {
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    // Bitwise OR accumulates all differences.
    // If any byte differs, result will be non-zero.
    result |= (a[i] ?? 0) ^ (b[i] ?? 0);
  }
  return result === 0;
}

/**
 * Compare two byte arrays in constant time.
 *
 * @param a - First byte array
 * @param b - Second byte array
 * @returns true if arrays are identical, false otherwise
 *
 * Note: The length check is NOT constant-time, but for fixed-size
 * cryptographic hashes (32 bytes for keccak256, 32 bytes for SHA-256d),
 * a length mismatch indicates a programming error, not an attack vector.
 */
export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }

  if (a.length === 0) {
    return true;
  }

  const nodeFn = getNodeTimingSafeEqual();
  if (nodeFn !== null) {
    return nodeFn(a, b);
  }

  return xorCompare(a, b);
}
